/* Mikel hero — single background video, dependency-free.
   Progressive enhancement: poster shows without JS; video fades in when it plays.

   Model:
   - The poster layer (.hero-poster) is the visible background and loads eagerly.
   - One <video> (.hero-video) ships with NO src; JS assigns the device-correct
     source (desktop vs mobile) so a device never downloads both videos.
   - On the video's `playing` event we add `video-is-playing` to the hero root,
     which fades the video in (z-index above) and fades the poster out (CSS).
   - On error / blocked autoplay / missing source the poster stays — never black.
   - Respects reduced motion, pauses when the tab is hidden or hero is offscreen. */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var mqMobile = window.matchMedia('(max-width: 767px)');

  /* Pick the correct source for this device without downloading the other. */
  function sourceFor(video) {
    var src = mqMobile.matches
      ? video.getAttribute('data-src-mobile')
      : video.getAttribute('data-src-desktop');
    if (!src) src = video.getAttribute('data-src-desktop') || video.getAttribute('data-src-mobile');
    return src || '';
  }

  function initHero(root) {
    if (root.dataset.mkHeroInit === '1') return;
    root.dataset.mkHeroInit = '1';

    var video = root.querySelector('.hero-video');
    if (!video) return; // image-only hero: poster is the final state

    if (reduceMotion) {
      // Reduced motion: never autoplay; the poster is the hero (also hidden via CSS).
      return;
    }

    var src = sourceFor(video);
    if (!src) {
      if (window.console) console.warn('[mk-hero] No video source found for this device; showing poster.');
      return;
    }

    // Fade the video in / poster out the moment playback actually starts.
    video.addEventListener('playing', function () {
      root.classList.add('video-is-playing');
    });

    // On failure keep the poster; surface a dev warning.
    video.addEventListener('error', function () {
      root.classList.remove('video-is-playing');
      if (window.console) console.warn('[mk-hero] Video failed to load; keeping poster.', src);
    });

    var attemptPlay = function () {
      video.muted = true; // required for autoplay in most browsers
      var p = video.play();
      if (p && p.catch) {
        p.catch(function (err) {
          if (window.console) console.warn('[mk-hero] Autoplay was blocked; keeping poster.', err);
        });
      }
    };

    video.muted = true;
    video.preload = 'auto';
    video.src = src;
    video.load();
    attemptPlay();

    // Pause/resume with tab visibility.
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) { video.pause(); }
      else { attemptPlay(); }
    });

    // Pause when scrolled offscreen, resume when back.
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) { attemptPlay(); }
          else { video.pause(); }
        });
      }, { threshold: 0.1 }).observe(root);
    }

    // Re-evaluate source if the viewport crosses the mobile breakpoint.
    var onBreakpoint = function () {
      var next = sourceFor(video);
      if (next && next !== video.src) {
        var wasPlaying = root.classList.contains('video-is-playing');
        video.src = next;
        video.load();
        if (wasPlaying) attemptPlay();
      }
    };
    if (mqMobile.addEventListener) mqMobile.addEventListener('change', onBreakpoint);
    else if (mqMobile.addListener) mqMobile.addListener(onBreakpoint);
  }

  function initAll() {
    document.querySelectorAll('[data-mk-hero]').forEach(initHero);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-init for Shopify Theme Editor section loads.
  document.addEventListener('shopify:section:load', function (e) {
    e.target.querySelectorAll('[data-mk-hero]').forEach(initHero);
  });
})();
